import { useContext } from 'react';
import { NotationWidgetState, NotationWidgetDispatch } from '../NotationWidget';
import { INotationWidgetFeatures } from '../Interfaces/INotationWidgetFeatures';
import { INotationWidgetRole } from '../Interfaces/INotationWidgetRole';
import React from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  TableBody,
  Checkbox,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { NotationWidgetActionType } from '../Types/NotationWidgetActionType';

interface IPermissionTableProps {
  type: string;
  features: INotationWidgetFeatures[];
  roles: INotationWidgetRole[];
}

const PermissionTable = (props: IPermissionTableProps) => {
  const state = useContext(NotationWidgetState);
  const dispatch = useContext(NotationWidgetDispatch);

  function decodePermission(role: any, permission: number) {
    return (role.value & permission) > 0 ? true : false;
  }

  function calculatePermissionValue(
    featureKey: string,
    permissionValue: number,
    checked: boolean,
  ) {
    if (props.type === 'Notation Type Select') {
      return checked
        ? featureKey === 'showAlerts'
          ? state.alertPermissions['showAlerts'] + permissionValue
          : featureKey === 'showUpdates'
          ? state.updatePermissions['showUpdates'] + permissionValue
          : featureKey === 'showEncouragements'
          ? state.encouragementPermissions['showEncouragements'] +
            permissionValue
          : 0
        : featureKey === 'showAlerts'
        ? state.alertPermissions['showAlerts'] - permissionValue
        : featureKey === 'showUpdates'
        ? state.updatePermissions['showUpdates'] - permissionValue
        : featureKey === 'showEncouragements'
        ? state.encouragementPermissions['showEncouragements'] - permissionValue
        : 0;
    } else {
      return checked
        ? props.type === 'Alerts'
          ? state.alertPermissions[featureKey] + permissionValue
          : props.type === 'Updates'
          ? state.updatePermissions[featureKey] + permissionValue
          : props.type === 'Encouragements'
          ? state.encouragementPermissions[featureKey] + permissionValue
          : 0
        : props.type === 'Alerts'
        ? state.alertPermissions[featureKey] - permissionValue
        : props.type === 'Updates'
        ? state.updatePermissions[featureKey] - permissionValue
        : props.type === 'Encouragements'
        ? state.encouragementPermissions[featureKey] - permissionValue
        : 0;
    }
  }

  function disabledHoverListener(
    type: string,
    role: INotationWidgetRole,
    feature: INotationWidgetFeatures,
  ): boolean | undefined {
    if (type === 'Notation Type Select') {
      return true;
    } else if (role.name === 'Anyone' && feature.key === 'assignTo') {
      return false;
    } else {
      return decodePermission(
        role,
        type === 'Alerts'
          ? state.alertPermissions['showAlerts']
          : type === 'Updates'
          ? state.updatePermissions['showUpdates']
          : state.encouragementPermissions['showEncouragements'],
      );
    }
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {props.roles.map((role, key) => (
                <TableCell key={key}>
                  {role.name}
                  {role.helper && (
                    <Tooltip title={role.helper} placement="top" arrow>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.features.map((feature, fkey) => (
              <TableRow key={fkey}>
                <TableCell>
                  Show {feature.name}
                  {feature.helper && (
                    <Tooltip title={feature.helper} placement="top" arrow>
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  )}
                </TableCell>
                {props.roles.map((role, rkey) => (
                  <TableCell key={rkey}>
                    <Tooltip
                      title={
                        role.name === 'Anyone' && feature.key === 'assignTo'
                          ? 'This feature is not accessible to this role'
                          : `Enable ${props.type} for the ${role.name} role to enable this feature`
                      }
                      placement="top"
                      arrow
                      disableHoverListener={disabledHoverListener(
                        props.type,
                        role,
                        feature,
                      )}
                    >
                      <span>
                        <Checkbox
                          disabled={
                            (role.name === 'Anyone' &&
                              feature.key === 'assignTo') ||
                            (props.type !== 'Notation Type Select' &&
                              !decodePermission(
                                role,
                                props.type === 'Alerts'
                                  ? state.alertPermissions['showAlerts']
                                  : props.type === 'Updates'
                                  ? state.updatePermissions['showUpdates']
                                  : state.encouragementPermissions[
                                      'showEncouragements'
                                    ],
                              ))
                          }
                          checked={
                            props.type === 'Notation Type Select'
                              ? decodePermission(
                                  role,
                                  feature.key === 'showAlerts'
                                    ? state.alertPermissions[feature.key]
                                    : feature.key === 'showUpdates'
                                    ? state.updatePermissions[feature.key]
                                    : state.encouragementPermissions[
                                        feature.key
                                      ],
                                )
                              : decodePermission(
                                  role,
                                  props.type === 'Alerts'
                                    ? state.alertPermissions[feature.key]
                                    : props.type === 'Updates'
                                    ? state.updatePermissions[feature.key]
                                    : state.encouragementPermissions[
                                        feature.key
                                      ],
                                )
                          }
                          onChange={async e => {
                            if (props.type === 'Notation Type Select') {
                              dispatch({
                                type:
                                  feature.key === 'showAlerts'
                                    ? NotationWidgetActionType.SetAlertPermissions
                                    : feature.key === 'showUpdates'
                                    ? NotationWidgetActionType.SetUpdatePermissions
                                    : NotationWidgetActionType.SetEncouragementPermissions,
                                permission: feature.key,
                                value: calculatePermissionValue(
                                  feature.key,
                                  role.value,
                                  e.target.checked,
                                ),
                              });
                            } else {
                              dispatch({
                                type:
                                  props.type === 'Alerts'
                                    ? NotationWidgetActionType.SetAlertPermissions
                                    : props.type === 'Updates'
                                    ? NotationWidgetActionType.SetUpdatePermissions
                                    : NotationWidgetActionType.SetEncouragementPermissions,
                                permission: feature.key,
                                value: calculatePermissionValue(
                                  feature.key,
                                  role.value,
                                  e.target.checked,
                                ),
                              });
                            }
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PermissionTable;
